import { create } from 'zustand';
import { useMemo } from 'react';
import type { Project, QualityIssue } from '../types';
import { FREE_DATA_QUALITY_CATEGORIES } from '../tierConfig';
import { useProjectStore } from './useProjectStore';
import { useTierAccess } from '../hooks/useTierAccess';
import { ApiService } from '../services/ApiService';

/**
 * #1775 — de projectbrede datakwaliteitsanalyse die App.tsx al in een Web Worker
 * draait (qualityWorker.ts), gedeeld met het detailpaneel, de stamboomkaarten en
 * de editor. Deze store rekent zelf niets: hij groepeert alleen de meldingen per
 * persoon. Alleen fouten en waarschuwingen; info (zoals "geen bronnen", bij bijna
 * iedereen) en duplicaten blijven op /quality.
 */
interface QualityState {
  issuesByPerson: Map<string, QualityIssue[]>;
  /** Volgnummer van de laatst naar de worker gestuurde analyse. */
  postedSeq: number;
  /** Wacht na opslaan in de editor op de eerste analyse die ná het opslaan is gestart. */
  watch: { personId: string; before: Set<string>; afterSeq: number } | null;
  /** Meldingen die door de laatste opslag zijn bijgekomen (voor de toast). */
  newIssues: { personId: string; issues: QualityIssue[] } | null;

  /** `seq` ontbreekt bij een reset (leeg project of > 2000 personen). */
  setAnalysis: (issues: QualityIssue[], seq?: number) => void;
  markPosted: () => number;
  watchPerson: (personId: string) => void;
  clearNewIssues: () => void;
}

export const useQualityStore = create<QualityState>((set, get) => ({
  issuesByPerson: new Map(),
  postedSeq: 0,
  watch: null,
  newIssues: null,

  setAnalysis: (issues, seq) =>
    set((s) => {
      const issuesByPerson = new Map<string, QualityIssue[]>();
      for (const issue of issues) {
        if (issue.type !== 'error' && issue.type !== 'warning') continue;
        for (const pid of issue.personIds) {
          const lijst = issuesByPerson.get(pid);
          if (lijst) lijst.push(issue);
          else issuesByPerson.set(pid, [issue]);
        }
      }
      // #1775 — een analyse die al liep vóór het opslaan telt niet: die rekent
      // nog met de oude gegevens. Pas een latere vergelijkt vóór met ná.
      if (!s.watch || seq === undefined || seq <= s.watch.afterSeq) return { issuesByPerson };
      const { personId, before } = s.watch;
      const nieuw = (issuesByPerson.get(personId) ?? []).filter((i) => !before.has(i.id));
      return {
        issuesByPerson,
        watch: null,
        newIssues: nieuw.length > 0 ? { personId, issues: nieuw } : s.newIssues,
      };
    }),

  markPosted: () => {
    const seq = get().postedSeq + 1;
    set({ postedSeq: seq });
    return seq;
  },

  watchPerson: (personId) => {
    const { issuesByPerson, postedSeq } = get();
    const before = new Set((issuesByPerson.get(personId) ?? []).map((i) => i.id));
    set({ watch: { personId, before, afterSeq: postedSeq } });
  },

  clearNewIssues: () => set({ newIssues: null }),
}));

/**
 * #1775 — dezelfde zeef als /quality: niet genegeerd, categorie niet
 * uitgeschakeld, en zonder het uitgebreide abonnement alleen datums en leeftijd.
 * Fouten eerst.
 */
export function zichtbareMeldingen(
  issues: QualityIssue[],
  settings: Project['settings'],
  uitgebreid: boolean
): QualityIssue[] {
  const genegeerd: string[] = settings?.acknowledgedIssues ?? [];
  const uit: string[] = settings?.qualityChecks?.disabledCategories ?? [];
  return issues
    .filter(
      (i) =>
        !genegeerd.includes(i.id) &&
        !uit.includes(i.category) &&
        (uitgebreid || (FREE_DATA_QUALITY_CATEGORIES as readonly string[]).includes(i.category))
    )
    .sort((a, b) => (a.type === b.type ? 0 : a.type === 'error' ? -1 : 1));
}

/** #1775 — meldingstekst zoals DataQualityChecker hem toont (dq_<ruleId> + details). */
export function meldingTekst(
  t: (key: string, params?: Record<string, string>) => string,
  issue: QualityIssue
): string {
  // EVENT_AFTER_DEATH / EVENT_BEFORE_BIRTH dragen een type-code (bv. 'residence');
  // vertalen zoals DataQualityChecker doet.
  const details = issue.details?.eventType
    ? { ...issue.details, eventType: t(issue.details.eventType) }
    : issue.details;
  return t(`dq_${issue.ruleId}`, details);
}

const GEEN: QualityIssue[] = [];

/** #1775 — zichtbare meldingen voor één persoon; `undefined` = niets ophalen. */
export function usePersonQualityIssues(personId: string | undefined): QualityIssue[] {
  const raw = useQualityStore((s) => (personId ? s.issuesByPerson.get(personId) : undefined));
  const settings = useProjectStore((s) => s.currentProject?.settings);
  const uitgebreid = useTierAccess().hasFeature('advancedDataQuality');
  return useMemo(
    () => (raw ? zichtbareMeldingen(raw, settings, uitgebreid) : GEEN),
    [raw, settings, uitgebreid]
  );
}

/**
 * #1775 — negeren via het bestaande `acknowledgedIssues` in project.settings,
 * eerst lokaal (de melding verdwijnt meteen), dan op de server (die voegt
 * settings op het hoogste niveau samen, repos/projects.js).
 */
export async function negeerMelding(issueId: string): Promise<void> {
  const project = useProjectStore.getState().currentProject;
  if (!project) return;
  const huidig: string[] = project.settings?.acknowledgedIssues ?? [];
  if (huidig.includes(issueId)) return;
  const acknowledgedIssues = [...huidig, issueId];
  useProjectStore.getState().setCurrentProject({
    ...project,
    settings: { ...(project.settings ?? {}), acknowledgedIssues },
  });
  await ApiService.updateProject(project.id, { settings: { acknowledgedIssues } }).catch((e) =>
    console.warn('Failed to persist acknowledged issues:', e)
  );
}
